/**
 * Translation Service using MyMemory Translation API
 * Free tier: 10,000 words/day, no API key required
 * API Documentation: https://mymemory.translated.net/doc/spec.php
 */

export type LanguageCode = 'en' | 'de' | 'tr' | 'fr' | 'es' | 'it' | 'pt' | 'ar' | 'hi' | 'zh';

interface TranslationResponse {
  responseData: {
    translatedText: string;
    match: number;
  };
  quotaFinished: boolean;
  mtLangSupported?: boolean;
  responseDetails?: string;
  responseStatus: number;
  responderId?: string;
  exception_code?: string;
  matches?: Array<{
    id: string;
    segment: string;
    translation: string;
    source: string;
    target: string;
    quality: number;
    reference?: string;
    'last-update-date': string;
    'match': number;
  }>;
}

// Language code mapping for MyMemory API
const languageCodeMap: Record<LanguageCode, string> = {
  en: 'en',
  de: 'de',
  tr: 'tr',
  fr: 'fr',
  es: 'es',
  it: 'it',
  pt: 'pt',
  ar: 'ar',
  hi: 'hi',
  zh: 'zh',
};

// Cache for translations to avoid redundant API calls
const translationCache = new Map<string, string>();

/**
 * Translate text using MyMemory Translation API
 * @param text - Text to translate
 * @param targetLang - Target language code
 * @param sourceLang - Source language code (default: 'en')
 * @returns Translated text
 */
export const translateText = async (
  text: string,
  targetLang: LanguageCode,
  sourceLang: LanguageCode = 'en'
): Promise<string> => {
  // Return original text if target is same as source
  if (targetLang === sourceLang || targetLang === 'en') {
    return text;
  }

  // Check cache
  const cacheKey = `${sourceLang}-${targetLang}-${text}`;
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey)!;
  }

  try {
    const sourceCode = languageCodeMap[sourceLang] || 'en';
    const targetCode = languageCodeMap[targetLang] || 'en';

    // MyMemory API endpoint
    const apiUrl = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${sourceCode}|${targetCode}`;

    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`Translation API error: ${response.status}`);
    }

    const data: TranslationResponse = await response.json();

    if (data.responseStatus === 200 && data.responseData?.translatedText) {
      const translatedText = data.responseData.translatedText;
      
      // Cache the translation
      translationCache.set(cacheKey, translatedText);
      
      return translatedText;
    } else {
      console.warn('Translation API returned unexpected response:', data);
      return text; // Return original text on error
    }
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original text on error
  }
};

/**
 * Translate multiple texts in batch
 * @param texts - Array of texts to translate
 * @param targetLang - Target language code
 * @param sourceLang - Source language code (default: 'en')
 * @returns Array of translated texts
 */
export const translateBatch = async (
  texts: string[],
  targetLang: LanguageCode,
  sourceLang: LanguageCode = 'en'
): Promise<string[]> => {
  // Translate in parallel with a small delay to respect rate limits
  const translations = await Promise.all(
    texts.map((text, index) => 
      new Promise<string>((resolve) => {
        setTimeout(() => {
          translateText(text, targetLang, sourceLang).then(resolve);
        }, index * 100); // Small delay between requests
      })
    )
  );
  
  return translations;
};

/**
 * Clear translation cache
 */
export const clearTranslationCache = () => {
  translationCache.clear();
};

